import { Injectable } from '@angular/core';
import { type ActivatedRouteSnapshot, Router, type RouterStateSnapshot, type UrlTree } from '@angular/router';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TokenStorageService } from '../services/token-storage.service';
import { PreferencesService } from '../services/preferences.service';

@Injectable()
export class AuthGuard  {
  publicRoutes = ['viewer/public/', 'login', 'register', 'not-found', 'forgot-password'];
  notAllowedWhileLoggedInRoutes = ['login', 'register'];
  adminRoutes = ['clinics', 'clinic-detail'];
  pmsIntegrationsRoutes = ['pms-integrations'];

  private readonly jwtHelper = new JwtHelperService();

  constructor(
    private readonly tokenService: TokenStorageService,
    private readonly router: Router,
    private readonly preferencesService: PreferencesService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    let token = this.tokenService.getToken();

    // Une session en cache peut survivre à son JWT : le cookie auth-token dure 90 jours
    // alors que le JWT expire en 7-30 jours. Un token expiré ne doit PAS être traité comme
    // une session valide, sinon l'app se charge, lance des requêtes que le backend rejette,
    // et l'UI se fige (spinner infini / dashboard vide) jusqu'à une déconnexion manuelle.
    // On le détecte ici et on le purge pour rediriger proprement vers /login.
    if (token && this.isTokenExpired(token)) {
      this.tokenService.signOut();
      token = '';
    }

    if (
      token &&
      token !== '' &&
      this.notAllowedWhileLoggedInRoutes.find(r => (route.routeConfig?.path ?? '---').startsWith(r))
    ) {
      // Permettre l'accès à /register si c'est pour compléter un profil SCC
      if (route.routeConfig?.path === 'register' && route.queryParams['usePassword'] === 'true') {
        return true;
      }

      void this.router.navigate(['/']);
      return false;
    }

    if (
      this.publicRoutes.find((r) =>
        (route.routeConfig?.path ?? '---').startsWith(r)
      )
    ) {
      return true;
    }

    if (!token || token === '') {
      if (route.routeConfig?.path === 'studies') {
        void this.router.navigate(['/login']);
      } else {
        void this.router.navigate(['/login'], {
          queryParams: { returnUrl: route.url.join('/') },
        });
      }

      return false;
    }

    if (this.adminRoutes.find(r => (route.routeConfig?.path ?? '---').startsWith(r))) {
      return this.preferencesService.getPreferences().pipe(
        map(prefs => {
          if (!prefs.securityInfo.claims.find(c => c === 'Clinic=Read')) {
            void this.router.navigate(['/']);
            return false;
          }

          return true;
        })
      );
    }

    if (route.routeConfig?.path === 'billing') {
      return this.preferencesService.getPreferences().pipe(
        map(prefs => {
          if (!prefs.securityInfo.claims.find(c => c === 'roles=Admin' || c === 'roles=ClinicAdmin')) {
            void this.router.navigate(['/']);
            return false;
          }

          return true;
        })
      );
    }

    if (route.routeConfig?.path === 'admin-dashboard') {
      return this.preferencesService.getPreferences().pipe(
        map(prefs => {
          if (!prefs.securityInfo.claims.find(c => c === 'roles=Admin')) {
            void this.router.navigate(['/']);
            return false;
          }

          return true;
        })
      );
    }

    // PMS Integrations routes require Admin or PmsDeveloper role
    if (this.pmsIntegrationsRoutes.find(r => (route.routeConfig?.path ?? '---').startsWith(r))) {
      return this.preferencesService.getPreferences().pipe(
        map(prefs => {
          if (!prefs.securityInfo.claims.find(c => c === 'roles=Admin' || c === 'roles=PmsDeveloper')) {
            void this.router.navigate(['/']);
            return false;
          }

          return true;
        })
      );
    }

    return true;
  }

  private isTokenExpired(token: string): boolean {
    try {
      return this.jwtHelper.isTokenExpired(token);
    } catch {
      // Token illisible/malformé → on force une reconnexion propre.
      return true;
    }
  }

}
